// Authorise Paid Days — one-action orchestration over attendance
// adjustments (pay_pending / delta auto-selected server-side).
//
// Flow: pick all-remaining or partial (half-day steps) → Preview
// (before → after Paid/LOP + Gross/Deductions/Net via engine dry-run)
// → reason → Authorise → adjustment saved (idempotency-keyed) →
// auto-recalculate → verify recalculated salary matches preview →
// "Completed". If governance parks the adjustment → "Awaiting
// approval". If recalculation/verification fails → "Incomplete" with
// a safe Retry (re-runs calculate + verify only, never re-applies).
// Closing the modal after any apply attempt refreshes the parent.
//
// Also exports RecallAuthorisationDialog — reverses an applied
// authorisation through the audit trail, then recalculates + verifies.
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle, Warning, ArrowsClockwise } from "@phosphor-icons/react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const daysEq = (a, b) => Math.abs(Number(a) - Number(b)) < 0.011;
const moneyEq = (a, b) => Math.abs(Number(a) - Number(b)) < 0.51;

function BeforeAfterCard({ preview }) {
    const b = preview.before;
    const a = preview.after;
    const Row = ({ label, bv, av, money }) => (
        <tr className="border-t">
            <td className="px-2 py-1 text-slate-500">{label}</td>
            <td className="px-2 py-1 text-right font-mono">{money ? inr(bv) : Number(bv).toFixed(1)}</td>
            <td className="px-2 py-1 text-right font-mono font-semibold text-emerald-800" data-testid={`apd-after-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                {money ? inr(av) : Number(av).toFixed(1)}
            </td>
        </tr>
    );
    return (
        <div className="border rounded-lg overflow-hidden" data-testid="apd-preview-card">
            <table className="w-full text-xs">
                <thead className="bg-slate-50">
                    <tr>
                        <th className="px-2 py-1 text-left"></th>
                        <th className="px-2 py-1 text-right">Before</th>
                        <th className="px-2 py-1 text-right text-emerald-700">After</th>
                    </tr>
                </thead>
                <tbody>
                    <Row label="Paid Days" bv={b.paid} av={a.paid} />
                    <Row label="LOP Days" bv={b.lop} av={a.lop} />
                    <Row label="Gross" bv={b.gross} av={a.gross} money />
                    <Row label="Deductions" bv={b.deductions} av={a.deductions} money />
                    <Row label="Net" bv={b.net} av={a.net} money />
                </tbody>
            </table>
            <div className="px-2 py-1.5 text-[11px] text-slate-600 border-t bg-slate-50">
                Adjustment mode: <Badge variant="outline" className="text-[10px]" data-testid="apd-mode">{preview.mode}</Badge>
                {preview.pending_portion > 0 && (
                    <span className="ml-2" data-testid="apd-pending-portion">
                        {preview.pending_portion} of {preview.days} day(s) are pending-regularization days (recorded as paid via this decision; attendance records stay unchanged)
                    </span>
                )}
            </div>
        </div>
    );
}

function PhaseStatus({ phase, msg }) {
    if (phase === "completed") {
        return (
            <div className="p-3 border border-emerald-200 bg-emerald-50 rounded text-xs text-emerald-900" data-testid="apd-status-completed">
                <CheckCircle size={14} className="inline mr-1" /><span className="font-semibold">Completed.</span> {msg}
            </div>
        );
    }
    if (phase === "awaiting_approval") {
        return (
            <div className="p-3 border border-amber-200 bg-amber-50 rounded text-xs text-amber-900" data-testid="apd-status-awaiting">
                <Warning size={14} className="inline mr-1" /><span className="font-semibold">Awaiting approval.</span> {msg}
            </div>
        );
    }
    if (phase === "incomplete") {
        return (
            <div className="p-3 border border-rose-200 bg-rose-50 rounded text-xs text-rose-900" data-testid="apd-status-incomplete">
                <Warning size={14} className="inline mr-1" /><span className="font-semibold">Incomplete.</span> {msg}
            </div>
        );
    }
    return (
        <div className="p-3 border border-sky-200 bg-sky-50 rounded text-xs text-sky-900" data-testid="apd-status-working">
            <ArrowsClockwise size={14} className="inline mr-1 animate-spin" />{msg}
        </div>
    );
}

export function AuthorisePaidDaysModal({ open, onClose, runId, employeeId, employeeLabel, reviseOf = null, onCompleted }) {
    const [daysChoice, setDaysChoice] = useState("all");
    const [partialDays, setPartialDays] = useState("");
    const [reason, setReason] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewErr, setPreviewErr] = useState("");
    const [phase, setPhase] = useState("form");
    const [statusMsg, setStatusMsg] = useState("");
    const idemKey = useMemo(() => (open ? crypto.randomUUID() : null), [open]);

    const days = daysChoice === "all" ? null : Number(partialDays);
    const working = ["applying", "recalculating", "verifying"].includes(phase);
    // Closing after any apply attempt refreshes the parent so the lines
    // table / header never show stale values behind the dialog.
    const handleClose = () => {
        if (phase !== "form") onCompleted?.();
        onClose();
    };

    const loadPreview = async (d) => {
        setPreviewErr("");
        try {
            const res = await api.post(
                `/payroll/run/${runId}/employee/${employeeId}/authorise-paid-days/preview`,
                { days: d, exclude_adjustment_id: reviseOf?.id || null },
            );
            setPreview(res.data);
        } catch (err) {
            setPreview(null);
            setPreviewErr(formatApiError(err));
        }
    };

    useEffect(() => {
        if (open) {
            setPhase("form"); setPreview(null); setPreviewErr("");
            setReason(""); setDaysChoice("all"); setPartialDays(""); setStatusMsg("");
            loadPreview(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, runId, employeeId, reviseOf?.id]);

    // Keep the preview card in sync while typing a partial day count
    // (debounced) so the card and the submit payload never diverge.
    useEffect(() => {
        if (!open || phase !== "form" || daysChoice !== "partial") return undefined;
        const v = Number(partialDays);
        if (!(v > 0)) return undefined;
        const t = setTimeout(() => loadPreview(v), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partialDays, daysChoice, open, phase]);

    const recalcAndVerify = async (pv) => {
        const base = pv || preview;
        setPhase("recalculating"); setStatusMsg("Recalculating payroll…");
        try {
            await api.post(`/payroll/run/${runId}/calculate`, {});
        } catch (err) {
            setPhase("incomplete");
            setStatusMsg(`Recalculation failed: ${formatApiError(err)} — the saved adjustment is intact; Retry is safe.`);
            return;
        }
        setPhase("verifying"); setStatusMsg("Verifying recalculated salary against preview…");
        try {
            const r = await api.get(`/payroll/run/${runId}/lines/${employeeId}`);
            const line = r.data?.line || r.data;
            const att = line.attendance_summary || {};
            const okDays = daysEq(att.paid_days, base.after.paid) && daysEq(att.lop_days, base.after.lop);
            const okMoney = moneyEq(line.gross_earnings, base.after.gross) && moneyEq(line.net_pay, base.after.net);
            if (okDays && okMoney) {
                setPhase("completed");
                setStatusMsg("Saved adjustment and recalculated salary match the preview.");
                toast.success("Paid days authorised");
                onCompleted?.();
            } else {
                setPhase("incomplete");
                setStatusMsg(`Recalculated values differ from preview (now ${att.paid_days}/${att.lop_days}, gross ${inr(line.gross_earnings)}). Retry is safe.`);
            }
        } catch (err) {
            setPhase("incomplete");
            setStatusMsg(`Verification failed: ${formatApiError(err)} — Retry is safe.`);
        }
    };

    const doApply = async () => {
        setPhase("applying"); setStatusMsg("Saving authorisation…");
        try {
            // Always re-fetch the preview at submit time so the verifier
            // baseline matches the submitted day count, even if the user
            // typed a partial count without clicking "Update preview".
            const pv = await api.post(
                `/payroll/run/${runId}/employee/${employeeId}/authorise-paid-days/preview`,
                { days, exclude_adjustment_id: reviseOf?.id || null },
            );
            const fresh = pv.data;
            setPreview(fresh);
            const res = await api.post(
                `/payroll/run/${runId}/employee/${employeeId}/authorise-paid-days`,
                {
                    days, reason, idempotency_key: idemKey,
                    replaces_adjustment_id: reviseOf?.id || null,
                },
            );
            const data = res.data || {};
            if (data.pending_approval) {
                setPhase("awaiting_approval");
                setStatusMsg(`Governance parked this authorisation for approval (AAS ${data.action_id}). After it is approved, click "Recalculate & Verify".`);
                return;
            }
            await recalcAndVerify(fresh);
        } catch (err) {
            setPhase("incomplete");
            setStatusMsg(`${formatApiError(err)} — no duplicate was written; Retry is safe.`);
        }
    };

    const remaining = preview?.remaining_unpaid ?? null;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !working) handleClose(); }}>
            <DialogContent className="max-w-lg" data-testid="apd-modal">
                <DialogHeader>
                    <DialogTitle>
                        {reviseOf ? "Revise Paid-Days Authorisation" : "Authorise Paid Days"}
                        {employeeLabel ? <span className="text-xs text-slate-500 font-normal ml-2">{employeeLabel}</span> : null}
                    </DialogTitle>
                </DialogHeader>

                {reviseOf && (
                    <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="apd-revise-note">
                        Replaces adjustment {reviseOf.id} — its effect is reversed through the audit trail first; no duplicate payment is stacked.
                    </div>
                )}

                {phase === "form" && (
                    <div className="space-y-3 mt-1">
                        <div className="space-y-1.5">
                            <Label className="text-xs">Days to authorise {remaining !== null ? <span className="text-slate-500">(remaining unpaid: {remaining})</span> : null}</Label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="radio" checked={daysChoice === "all"} onChange={() => { setDaysChoice("all"); loadPreview(null); }} data-testid="apd-days-all" />
                                All remaining unpaid days
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="radio" checked={daysChoice === "partial"} onChange={() => setDaysChoice("partial")} data-testid="apd-days-partial" />
                                Partial
                                <Input
                                    type="number" step="0.5" min="0.5" max={remaining ?? undefined}
                                    value={partialDays} disabled={daysChoice !== "partial"}
                                    onChange={(e) => setPartialDays(e.target.value)}
                                    className="w-24 h-8" data-testid="apd-days-input"
                                />
                                <span className="text-xs text-slate-500">half-day steps allowed</span>
                            </label>
                            {daysChoice === "partial" && (
                                <Button size="sm" variant="outline" onClick={() => loadPreview(Number(partialDays) || null)} data-testid="apd-preview-refresh">
                                    Update preview
                                </Button>
                            )}
                        </div>

                        {previewErr && <div className="text-xs text-rose-700" data-testid="apd-preview-error">{previewErr}</div>}
                        {preview && <BeforeAfterCard preview={preview} />}

                        <div className="space-y-1">
                            <Label className="text-xs">Reason (audit trail, min 10 chars)</Label>
                            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="apd-reason" />
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={handleClose} data-testid="apd-cancel">Cancel</Button>
                            <Button
                                onClick={doApply}
                                disabled={!preview || reason.trim().length < 10 || (daysChoice === "partial" && !(Number(partialDays) > 0))}
                                data-testid="apd-submit"
                            >
                                Authorise
                            </Button>
                        </div>
                    </div>
                )}

                {phase !== "form" && (
                    <div className="space-y-3 mt-1">
                        <PhaseStatus phase={phase} msg={statusMsg} />
                        <div className="flex justify-end gap-2">
                            {phase === "incomplete" && (
                                <Button onClick={recalcAndVerify} data-testid="apd-retry">
                                    <ArrowsClockwise size={13} className="mr-1" />Retry recalculate & verify
                                </Button>
                            )}
                            {phase === "awaiting_approval" && (
                                <Button onClick={recalcAndVerify} data-testid="apd-verify-after-approval">Recalculate & Verify</Button>
                            )}
                            {(phase === "completed" || phase === "awaiting_approval" || phase === "incomplete") && (
                                <Button variant="outline" onClick={handleClose} data-testid="apd-close">Close</Button>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export function RecallAuthorisationDialog({ open, onClose, runId, employeeId, adjustment, onCompleted }) {
    const [reason, setReason] = useState("");
    const [phase, setPhase] = useState("form");
    const [statusMsg, setStatusMsg] = useState("");
    const working = ["applying", "recalculating", "verifying"].includes(phase);
    const handleClose = () => {
        if (phase !== "form") onCompleted?.();
        onClose();
    };

    useEffect(() => {
        if (open) { setPhase("form"); setReason(""); setStatusMsg(""); }
    }, [open, adjustment?.id]);

    const verify = async () => {
        const sys = adjustment?.system_value || {};
        const r = await api.get(`/payroll/run/${runId}/lines/${employeeId}`);
        const line = r.data?.line || r.data;
        const att = line.attendance_summary || {};
        return daysEq(att.paid_days, sys.paid_days) && daysEq(att.lop_days, sys.lop_days);
    };

    const run = async () => {
        setPhase("applying"); setStatusMsg("Reversing the authorisation through the audit trail…");
        try {
            await api.post(
                `/payroll/run/${runId}/employee/${employeeId}/adjustment/${adjustment.id}/reverse`,
                { reason },
            );
        } catch (err) {
            setPhase("incomplete"); setStatusMsg(`${formatApiError(err)} — nothing was changed; Retry is safe.`);
            return;
        }
        setPhase("recalculating"); setStatusMsg("Recalculating payroll…");
        try {
            await api.post(`/payroll/run/${runId}/calculate`, {});
        } catch (err) {
            setPhase("incomplete"); setStatusMsg(`Reversal saved, but recalculation failed: ${formatApiError(err)} — Retry is safe.`);
            return;
        }
        setPhase("verifying"); setStatusMsg("Verifying the line returned to its pre-authorisation state…");
        try {
            if (await verify()) {
                setPhase("completed"); setStatusMsg("Reversal saved and recalculated salary matches the pre-authorisation state.");
                toast.success("Authorisation recalled");
                onCompleted?.();
            } else {
                setPhase("incomplete"); setStatusMsg("Recalculated values do not match the pre-authorisation state yet. Retry is safe.");
            }
        } catch (err) {
            setPhase("incomplete"); setStatusMsg(`Verification failed: ${formatApiError(err)} — Retry is safe.`);
        }
    };

    const retryVerify = async () => {
        setPhase("recalculating"); setStatusMsg("Recalculating payroll…");
        try { await api.post(`/payroll/run/${runId}/calculate`, {}); }
        catch (err) { setPhase("incomplete"); setStatusMsg(`Recalculation failed: ${formatApiError(err)} — Retry is safe.`); return; }
        setPhase("verifying"); setStatusMsg("Verifying…");
        try {
            if (await verify()) {
                setPhase("completed"); setStatusMsg("Reversal saved and recalculated salary matches the pre-authorisation state.");
                onCompleted?.();
            } else {
                setPhase("incomplete"); setStatusMsg("Still not matching. Retry is safe.");
            }
        } catch (err) { setPhase("incomplete"); setStatusMsg(`Verification failed: ${formatApiError(err)}`); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !working) handleClose(); }}>
            <DialogContent className="max-w-md" data-testid="recall-dialog">
                <DialogHeader>
                    <DialogTitle>Recall Authorisation <span className="text-xs text-slate-500 font-normal ml-1">{adjustment?.id}</span></DialogTitle>
                </DialogHeader>
                {phase === "form" ? (
                    <div className="space-y-3 mt-1">
                        <div className="text-xs text-slate-600">
                            Reverses this decision through the audit trail (append-only reversal row), then recalculates automatically.
                            Paid/LOP returns to {Number(adjustment?.system_value?.paid_days ?? 0)} / {Number(adjustment?.system_value?.lop_days ?? 0)}.
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Reason (audit trail, min 10 chars)</Label>
                            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="recall-reason" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={handleClose} data-testid="recall-cancel">Cancel</Button>
                            <Button variant="destructive" onClick={run} disabled={reason.trim().length < 10} data-testid="recall-confirm">Recall & Recalculate</Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3 mt-1">
                        <PhaseStatus phase={phase} msg={statusMsg} />
                        <div className="flex justify-end gap-2">
                            {phase === "incomplete" && (
                                <Button onClick={retryVerify} data-testid="recall-retry"><ArrowsClockwise size={13} className="mr-1" />Retry</Button>
                            )}
                            {(phase === "completed" || phase === "incomplete") && (
                                <Button variant="outline" onClick={handleClose} data-testid="recall-close">Close</Button>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default AuthorisePaidDaysModal;
